import {
  AddCommentIconData,
  AddIconData,
  AddReactionIconData,
  ArrowOutwardIconData,
  BrushIconData,
  CategoryIconData,
  CircleIconData,
  Crop169IconData,
  FormatAlignCenterIconData,
  FormatAlignLeftIconData,
  FormatAlignRightIconData,
  GridOffIconData,
  GridOnIconData,
  MouseIconData,
  SelectAllIconData,
  StickyNote2IconData,
  SwapHorizIconData,
  TitleIconData,
} from "@meiden-connect/material-symbols";
import { BinarySwitch, Button, Icon } from "@meiden-connect/ui";
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import type { EmojiStampKind, TextAlign } from "@refinement-canvas/domain";
import { useCanvasStore, type CanvasTool, type ShapeKind } from "../store/canvas-store";
import { useCtrlWheelZoom } from "../input/use-ctrl-wheel-zoom";
import { STAMP_KIND_GLYPH, STAMP_KIND_LABEL } from "../objects/stamp-object";
import { useCurrentActor } from "../../../auth/use-current-actor";
import { isSnapshotAvatarImageUrl } from "../../../auth/avatar-image-url";
import { CommentAvatar } from "../comments/comment-avatar";
import {
  addPenCustomColor,
  isValidHexColor,
  loadPenCustomColors,
  normalizeHexColor,
  PEN_PRESET_COLORS,
  PEN_THICKNESS_PIXELS,
  savePenColor,
  type PenColor,
  type PenThickness,
} from "../../../lib/pen-preference";
import {
  addStickyCustomColor,
  loadStickyCustomColors,
  saveStickyColor,
  STICKY_PRESET_COLORS,
  type StickyColor,
} from "../../../lib/sticky-preference";
import {
  addShapeCustomColor,
  loadShapeCustomColors,
  saveShapeFill,
  SHAPE_FILL_NONE,
  SHAPE_FILL_PRESET_COLORS,
  type ShapeFill,
} from "../../../lib/shape-preference";
import {
  addSectionCustomColor,
  loadSectionCustomColors,
  SECTION_FILL_PRESET_COLORS,
} from "../../../lib/section-preference";
import {
  clampTextFontSize,
  resolveTextSizeKind,
  TEXT_SIZE_MAX_PX,
  TEXT_SIZE_MIN_PX,
  TEXT_SIZE_PRESET_PX,
  type TextSizeKind,
} from "../../../lib/text-size-preference";

// 下部中央のフローティングツールバー。
// 図形・ペン・付箋はサブ選択パネルで種類や色を選択できる。
// ペン・付箋の色はボタンではなく丸い色サンプルの直接選択 UI とし、右端の「＋」で
// 任意のカラーパレットからカスタム色を 1 件追加できる（追加色はローカル保持）。
export function ToolBar(): JSX.Element {
  const tool = useCanvasStore((s) => s.tool);
  const setTool = useCanvasStore((s) => s.setTool);
  const shapeKind = useCanvasStore((s) => s.shapeKind);
  const setShapeKind = useCanvasStore((s) => s.setShapeKind);
  const penColor = useCanvasStore((s) => s.penColor);
  const setPenColor = useCanvasStore((s) => s.setPenColor);
  const penThickness = useCanvasStore((s) => s.penThickness);
  const setPenThickness = useCanvasStore((s) => s.setPenThickness);
  const stickyColor = useCanvasStore((s) => s.stickyColor);
  const setStickyColor = useCanvasStore((s) => s.setStickyColor);
  const shapeFill = useCanvasStore((s) => s.shapeFill);
  const setShapeFill = useCanvasStore((s) => s.setShapeFill);
  const sectionFill = useCanvasStore((s) => s.sectionFill);
  const setSectionFill = useCanvasStore((s) => s.setSectionFill);
  const stampKind = useCanvasStore((s) => s.stampKind);
  const setStampKind = useCanvasStore((s) => s.setStampKind);
  const textFontSize = useCanvasStore((s) => s.textFontSize);
  const setTextFontSize = useCanvasStore((s) => s.setTextFontSize);
  const textAlign = useCanvasStore((s) => s.textAlign);
  const setTextAlign = useCanvasStore((s) => s.setTextAlign);
  const gridSnapEnabled = useCanvasStore((s) => s.gridSnapEnabled);
  const setGridSnapEnabled = useCanvasStore((s) => s.setGridSnapEnabled);
  const invertSelectPan = useCanvasStore((s) => s.invertSelectPan);
  const setInvertSelectPan = useCanvasStore((s) => s.setInvertSelectPan);
  // ツールバー / サブパネル上での Ctrl+ホイールもキャンバスズームに振り替える（ブラウザ拡大を防ぐ）。
  const shellRef = useRef<HTMLDivElement | null>(null);
  useCtrlWheelZoom(shellRef);

  // アバター画像を設定済みの利用者だけ、自分のアイコンをスタンプとして選べるようにする。
  // 「選べる条件」を「保存してよい条件」（data:image/ スナップショット）へ揃える。avatar スタンプは
  // 配置時点の自己完結スナップショットのみ保存するため、可変・失効し得るリモート https アバターの
  // 利用者には選択肢を出さない（出してもサーバで弾かれ、見た目も後から変わってしまうため）。
  // useCurrentActor().avatarImageUrl は usePortableAvatarImageUrl が idp-file: 等を縮小 data URL 化した
  // ポータブル値なので、典型的な IdP アバター（idp-file:）は解決後ここで data: と判定され候補に出る。
  const actor = useCurrentActor();
  const avatarStampAvailable = isSnapshotAvatarImageUrl(actor.avatarImageUrl);

  // アバターが利用不可へ遷移（再解決失敗・認証復旧中の一時欠落など）したのに stampKind が "avatar" の
  // ままだと、ボタンが非表示で選択状態が見えず、配置時に無言で既定の絵文字へフォールバックして
  // 「アバターを選んだのに別スタンプが置かれる」食い違いになる。利用不可になったら選択を既定の絵文字へ
  // 戻し、ツールバー表示と実際の生成物を一致させる。
  useEffect(() => {
    if (stampKind === "avatar" && !avatarStampAvailable) {
      setStampKind("thumbs-up");
    }
  }, [stampKind, avatarStampAvailable, setStampKind]);

  // カスタム色一覧はローカルストレージで保持しつつ React state に二重持ちし、
  // 「追加 → 即座に丸として表示」を React 経由で反映できるようにする。
  const [penCustomColors, setPenCustomColors] = useState<readonly PenColor[]>(() =>
    loadPenCustomColors()
  );
  const [stickyCustomColors, setStickyCustomColors] = useState<readonly StickyColor[]>(() =>
    loadStickyCustomColors()
  );
  const [shapeCustomColors, setShapeCustomColors] = useState<readonly string[]>(() =>
    loadShapeCustomColors()
  );
  const [sectionCustomColors, setSectionCustomColors] = useState<readonly string[]>(() =>
    loadSectionCustomColors()
  );

  // 他タブで色を追加された場合に追従できるよう、storage イベントで再ロードする。
  useEffect(() => {
    const onStorage = (event: StorageEvent): void => {
      if (event.key === null || event.key.startsWith("refinement-canvas:pen:custom-colors")) {
        setPenCustomColors(loadPenCustomColors());
      }
      if (event.key === null || event.key.startsWith("refinement-canvas:sticky:custom-colors")) {
        setStickyCustomColors(loadStickyCustomColors());
      }
      if (event.key === null || event.key.startsWith("refinement-canvas:shape:custom-colors")) {
        setShapeCustomColors(loadShapeCustomColors());
      }
      if (event.key === null || event.key.startsWith("refinement-canvas:section:custom-colors")) {
        setSectionCustomColors(loadSectionCustomColors());
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // ショートカットキーは use-global-shortcuts の単一キー割当と一致させる。
  // title 属性ではショートカットキーを併記して、利用者がキー操作に気付けるようにする。
  const buttons: { tool: CanvasTool; label: string; icon: string; shortcut: string }[] = [
    { tool: "place-sticky", label: "付箋を配置", icon: StickyNote2IconData, shortcut: "F" },
    { tool: "place-section", label: "セクションを配置", icon: SelectAllIconData, shortcut: "N" },
    { tool: "place-shape", label: "図形を配置", icon: CategoryIconData, shortcut: "R" },
    { tool: "place-text", label: "テキストを配置", icon: TitleIconData, shortcut: "T" },
    { tool: "draw-pen", label: "ペンで描画", icon: BrushIconData, shortcut: "M" },
    { tool: "place-stamp", label: "スタンプを配置", icon: AddReactionIconData, shortcut: "E" },
    { tool: "place-comment", label: "コメントを追加", icon: AddCommentIconData, shortcut: "C" },
  ];

  const choosePenColor = (color: PenColor): void => {
    setPenColor(color);
    savePenColor(color);
  };
  const chooseStickyColor = (color: StickyColor): void => {
    setStickyColor(color);
    saveStickyColor(color);
  };
  const chooseShapeFill = (fill: ShapeFill): void => {
    setShapeFill(fill);
    saveShapeFill(fill);
  };
  // セクションの背景色は store の setSectionFill が localStorage への保存まで行う
  // （sticky / shape と異なり choose ヘルパーでの save 呼び出しは不要）。

  // 「＋」を押した時のカスタム色追加。<input type="color"> のネイティブピッカーで
  // 選択させ、確定（change）時にローカルストレージへ追加して即座にツールバーへ反映する。
  const handleAddPenColor = (value: string): void => {
    if (!isValidHexColor(value)) return;
    const normalized = normalizeHexColor(value);
    const next = addPenCustomColor(normalized);
    setPenCustomColors(next);
    // 追加直後に選択状態へ昇格させ、利用者の意図どおりすぐ描画に使えるようにする。
    choosePenColor(normalized);
  };
  const handleAddStickyColor = (value: string): void => {
    if (!isValidHexColor(value)) return;
    const normalized = normalizeHexColor(value);
    const next = addStickyCustomColor(normalized);
    setStickyCustomColors(next);
    chooseStickyColor(normalized);
  };
  const handleAddShapeColor = (value: string): void => {
    if (!isValidHexColor(value)) return;
    const normalized = normalizeHexColor(value);
    const next = addShapeCustomColor(normalized);
    setShapeCustomColors(next);
    chooseShapeFill(normalized);
  };
  const handleAddSectionColor = (value: string): void => {
    if (!isValidHexColor(value)) return;
    const normalized = normalizeHexColor(value);
    const next = addSectionCustomColor(normalized);
    setSectionCustomColors(next);
    setSectionFill(normalized);
  };

  return (
    <div ref={shellRef} className="canvas-toolbar-shell" role="group" aria-label="キャンバスツール">
      {tool === "place-shape" ? (
        <>
          <SubPanel label="図形の種類">
            {SHAPE_OPTIONS.map((option) => (
              <Button
                key={option.kind}
                type="button"
                variant={shapeKind === option.kind ? "action emphasis" : "action"}
                aria-label={option.label}
                aria-pressed={shapeKind === option.kind}
                title={option.label}
                onClick={() => setShapeKind(option.kind)}
              >
                <Icon d={option.icon} />
              </Button>
            ))}
          </SubPanel>
          {/* 矢印は塗りつぶせる領域を持たない（線＋矢じり）ため、背景色は丸 / 四角のみで提供する。 */}
          {shapeKind !== "arrow" ? (
            <SubPanel label="背景色">
              <ColorSwatchList
                ariaLabel="図形の背景色"
                presetColors={SHAPE_FILL_PRESET_COLORS}
                customColors={shapeCustomColors}
                value={shapeFill}
                onChoose={chooseShapeFill}
                onAddColor={handleAddShapeColor}
                noneOption={{ value: SHAPE_FILL_NONE, label: "背景なし" }}
              />
            </SubPanel>
          ) : null}
        </>
      ) : null}
      {tool === "place-section" ? (
        <SubPanel label="背景色">
          <ColorSwatchList
            ariaLabel="セクションの背景色"
            presetColors={SECTION_FILL_PRESET_COLORS}
            customColors={sectionCustomColors}
            value={sectionFill}
            onChoose={setSectionFill}
            onAddColor={handleAddSectionColor}
          />
        </SubPanel>
      ) : null}
      {tool === "draw-pen" ? (
        <>
          <SubPanel label="ペンの色">
            <ColorSwatchList
              ariaLabel="ペンの色"
              presetColors={PEN_PRESET_COLORS}
              customColors={penCustomColors}
              value={penColor}
              onChoose={choosePenColor}
              onAddColor={handleAddPenColor}
            />
          </SubPanel>
          <SubPanel label="太さ">
            <PenThicknessChooser
              value={penThickness}
              color={penColor}
              onChange={setPenThickness}
            />
          </SubPanel>
        </>
      ) : null}
      {tool === "place-sticky" ? (
        <>
          <SubPanel label="付箋の色">
            <ColorSwatchList
              ariaLabel="付箋の色"
              presetColors={STICKY_PRESET_COLORS}
              customColors={stickyCustomColors}
              value={stickyColor}
              onChoose={chooseStickyColor}
              onAddColor={handleAddStickyColor}
            />
          </SubPanel>
          <SubPanel label="文字寄せ">
            <TextAlignChooser value={textAlign} onChange={setTextAlign} />
          </SubPanel>
        </>
      ) : null}
      {tool === "place-text" ? (
        <>
          <SubPanel label="文字サイズ">
            <TextSizeChooser value={textFontSize} onChange={setTextFontSize} />
          </SubPanel>
          <SubPanel label="文字寄せ">
            <TextAlignChooser value={textAlign} onChange={setTextAlign} />
          </SubPanel>
        </>
      ) : null}
      {tool === "place-stamp" ? (
        <SubPanel label="スタンプ">
          <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
            {STAMP_KIND_ORDER.map((kind) => (
              <Button
                key={kind}
                type="button"
                variant={stampKind === kind ? "action emphasis" : "action"}
                aria-label={STAMP_KIND_LABEL[kind]}
                aria-pressed={stampKind === kind}
                title={STAMP_KIND_LABEL[kind]}
                onClick={() => setStampKind(kind)}
              >
                <span style={{ fontSize: "18px", lineHeight: 1 }}>{STAMP_KIND_GLYPH[kind]}</span>
              </Button>
            ))}
            {/* アバター設定済みの利用者のみ、自分のアイコン画像をスタンプとして選択できる。 */}
            {avatarStampAvailable ? (
              <Button
                type="button"
                variant={stampKind === "avatar" ? "action emphasis" : "action"}
                aria-label={STAMP_KIND_LABEL.avatar}
                aria-pressed={stampKind === "avatar"}
                title="自分のアバターをスタンプにする"
                onClick={() => setStampKind("avatar")}
              >
                <CommentAvatar
                  name={actor.displayName}
                  src={actor.avatarImageUrl}
                  size={20}
                  isSelf
                />
              </Button>
            ) : null}
          </div>
        </SubPanel>
      ) : null}
      <div role="toolbar" aria-label="キャンバスツールバー" className="canvas-toolbar">
        {/* グリッド吸着の ON/OFF。付箋ボタンの左に配置し、設定はローカルストレージに保存する。 */}
        <BinarySwitch
          aria-label="グリッド吸着の有効/無効"
          className="canvas-grid-snap-switch"
          data-testid="grid-snap-switch"
          onChange={setGridSnapEnabled}
          options={[
            { d: GridOffIconData, value: false },
            { d: GridOnIconData, value: true },
          ]}
          title={gridSnapEnabled ? "グリッド吸着: ON (G)" : "グリッド吸着: OFF (G)"}
          value={gridSnapEnabled}
        />
        {/* 選択/パン操作の左右反転。ON で左ドラッグ=範囲選択・右ドラッグ=キャンバス移動になる。 */}
        <BinarySwitch
          aria-label="選択/パン操作の左右反転"
          className="canvas-select-pan-invert-switch"
          data-testid="select-pan-invert-switch"
          onChange={setInvertSelectPan}
          options={[
            { d: MouseIconData, value: false },
            { d: SwapHorizIconData, value: true },
          ]}
          title={invertSelectPan ? "左右ドラッグ反転: ON (H)" : "左右ドラッグ反転: OFF (H)"}
          value={invertSelectPan}
        />
        {buttons.map((b) => (
          <Button
            key={b.tool}
            type="button"
            aria-label={b.label}
            aria-pressed={tool === b.tool}
            title={`${b.label} (${b.shortcut})`}
            onClick={() => setTool(tool === b.tool ? "idle" : b.tool)}
            data-testid={`tool-button-${b.tool}`}
            // クリック直後に配置が完了せず、配置モードへ入る準備操作なので intent を使う。
            // 選択中のツールは emphasis で強調し、現在のツールが分かるようにする。
            // 枠なし＋ホバー背景はデザインシステムのアイコンボタンに合わせて canvas-icon-button で付与する。
            className="canvas-icon-button"
            variant={tool === b.tool ? "intent emphasis" : "intent"}
          >
            <Icon d={b.icon} />
          </Button>
        ))}
      </div>
    </div>
  );
}

function SubPanel({ children, label }: { readonly children: ReactNode; readonly label: string }): JSX.Element {
  return (
    <div className="canvas-toolbar-subpanel" aria-label={label}>
      <span className="canvas-toolbar-subpanel-label">{label}</span>
      <div className="canvas-toolbar-subpanel-actions">{children}</div>
    </div>
  );
}

// 色のサンプル丸ボタン群。
// プリセット色 → カスタム色 → 「＋」追加ボタン の順に並べる。
// 選択中の丸には外側の青い円形リングを描いて選択状態を強調する（添付イメージ準拠）。
function ColorSwatchList({
  ariaLabel,
  presetColors,
  customColors,
  value,
  onChoose,
  onAddColor,
  noneOption,
}: {
  readonly ariaLabel: string;
  readonly presetColors: readonly string[];
  readonly customColors: readonly string[];
  readonly value: string;
  readonly onChoose: (color: string) => void;
  readonly onAddColor: (color: string) => void;
  // 「なし」（背景なし等）を表す先頭のサンプル。指定時のみ表示する。
  readonly noneOption?: { readonly value: string; readonly label: string } | undefined;
}): JSX.Element {
  // value が「なし」センチネル（hex でない）の場合に normalizeHexColor へ渡すと別の文字列へ
  // 化けるため、hex のときだけ正規化する。色見本の選択判定にだけ使う。
  const normalizedValue = isValidHexColor(value) ? normalizeHexColor(value) : value;
  // 重複排除のため、プリセットに既に含まれている custom 色は表示しない（loadX で既に正規化済み）。
  const visibleCustom = customColors.filter((c) => !presetColors.includes(c));

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "6px",
        alignItems: "center",
        maxWidth: "440px",
      }}
    >
      {noneOption ? (
        <NoneSwatch
          label={noneOption.label}
          selected={value === noneOption.value}
          onClick={() => onChoose(noneOption.value)}
        />
      ) : null}
      {presetColors.map((color) => (
        <ColorSwatch
          key={`preset-${color}`}
          color={color}
          selected={normalizeHexColor(color) === normalizedValue}
          onClick={() => onChoose(color)}
        />
      ))}
      {visibleCustom.map((color) => (
        <ColorSwatch
          key={`custom-${color}`}
          color={color}
          selected={normalizeHexColor(color) === normalizedValue}
          onClick={() => onChoose(color)}
        />
      ))}
      <AddColorSwatch onPick={onAddColor} />
    </div>
  );
}

// 色サンプル 1 件分。<input type="radio"> をベースに視覚は丸い色見本へ差し替える。
function ColorSwatch({
  color,
  selected,
  onClick,
}: {
  readonly color: string;
  readonly selected: boolean;
  readonly onClick: () => void;
}): JSX.Element {
  const swatchStyle: CSSProperties = {
    position: "relative",
    width: "26px",
    height: "26px",
    borderRadius: "50%",
    background: color,
    border: "1px solid rgba(0,0,0,0.08)",
    cursor: "pointer",
    padding: 0,
    boxShadow: selected ? "0 0 0 3px var(--color-accent, #1e63d4)" : undefined,
    outline: "none",
  };
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-label={color}
      title={color}
      onClick={onClick}
      style={swatchStyle}
    />
  );
}

// 「背景なし」を表す色見本。白地に赤い斜線を引いて「なし」を視覚化する。
function NoneSwatch({
  label,
  selected,
  onClick,
}: {
  readonly label: string;
  readonly selected: boolean;
  readonly onClick: () => void;
}): JSX.Element {
  const swatchStyle: CSSProperties = {
    position: "relative",
    width: "26px",
    height: "26px",
    borderRadius: "50%",
    // 白地に細い赤の斜線（左下→右上）を引いて「なし」を表現する。
    background:
      "linear-gradient(to top right, transparent calc(50% - 1px), #d22b2b calc(50% - 1px), #d22b2b calc(50% + 1px), transparent calc(50% + 1px)), #fff",
    border: "1px solid rgba(0,0,0,0.18)",
    cursor: "pointer",
    padding: 0,
    boxShadow: selected ? "0 0 0 3px var(--color-accent, #1e63d4)" : undefined,
    outline: "none",
  };
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-label={label}
      title={label}
      onClick={onClick}
      style={swatchStyle}
    />
  );
}

// 「＋」ボタンに見える色追加トリガ。実体は隠した <input type="color">。
// クリックでネイティブカラーピッカーを開き、change で hex 値を上位へ通知する。
function AddColorSwatch({ onPick }: { readonly onPick: (color: string) => void }): JSX.Element {
  return (
    <label
      title="カスタム色を追加"
      aria-label="カスタム色を追加"
      style={{
        position: "relative",
        width: "26px",
        height: "26px",
        borderRadius: "50%",
        border: "1.5px dashed var(--color-border, #b6b6c6)",
        display: "grid",
        placeItems: "center",
        cursor: "pointer",
        color: "var(--color-text-muted, #6b6f7d)",
        background: "transparent",
      }}
    >
      <Icon d={AddIconData} />
      <input
        type="color"
        // 透明 + 大きさ 0 でネイティブピッカーだけを利用する。クリックは label のヒット領域で拾う。
        style={{
          position: "absolute",
          inset: 0,
          opacity: 0,
          width: "100%",
          height: "100%",
          padding: 0,
          border: "none",
          cursor: "pointer",
        }}
        onChange={(event) => onPick(event.currentTarget.value)}
      />
    </label>
  );
}

// 文字サイズの 2 段階目選択 UI。
// 小・中・大 のプリセットボタンと、「px」入力（数値スピン）を横並びで提供する。
// プリセット値と一致する px の場合は該当ボタンを選択状態として強調する。
// px 入力は途中の文字列を local state（draft）で保持し、blur / Enter / 矢印操作で
// 確定時にだけ clamp して onChange へ反映する。これをしないと、たとえば「24」と打つ
// 途中の「2」が即 clamp（=8）されて以降のキーストロークが破棄されてしまうため、
// 利用者が複数桁の値を入力できなくなる。
function TextSizeChooser({
  value,
  onChange,
}: {
  readonly value: number;
  readonly onChange: (px: number) => void;
}): JSX.Element {
  const kind = resolveTextSizeKind(value);
  // value（外部）と pxDraft（ローカル文字列）を二重持ちし、外部値の更新があれば draft を
  // 同期する。利用者の途中入力中は draft が外部値より先行する。
  const [pxDraft, setPxDraft] = useState<string>(() => String(value));
  useEffect(() => {
    setPxDraft(String(value));
  }, [value]);

  const commitDraft = (): void => {
    const next = clampTextFontSize(Number.parseInt(pxDraft, 10));
    setPxDraft(String(next));
    if (next !== value) onChange(next);
  };

  const presetButton = (id: TextSizeKind, label: string, px: number): JSX.Element => (
    <Button
      key={id}
      type="button"
      variant={kind === id ? "action emphasis" : "action"}
      aria-label={`文字サイズ: ${label}（${px}px）`}
      aria-pressed={kind === id}
      title={`${label}（${px}px）`}
      onClick={() => onChange(px)}
    >
      <span style={{ minWidth: "1.2em", textAlign: "center" }}>{label}</span>
    </Button>
  );
  return (
    <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
      {presetButton("small", "小", TEXT_SIZE_PRESET_PX.small)}
      {presetButton("medium", "中", TEXT_SIZE_PRESET_PX.medium)}
      {presetButton("large", "大", TEXT_SIZE_PRESET_PX.large)}
      <label
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          fontSize: "12px",
          color: "var(--color-text-muted, #6b6f7d)",
        }}
        title="px 指定"
      >
        <input
          type="number"
          inputMode="numeric"
          min={TEXT_SIZE_MIN_PX}
          max={TEXT_SIZE_MAX_PX}
          step={1}
          value={pxDraft}
          aria-label="文字サイズ（px 指定）"
          onChange={(event) => {
            // 途中の入力（例: "24" を打つ途中の "2"）も draft として受け入れる。
            // clamp は blur / Enter / 矢印操作の確定時に行う。
            setPxDraft(event.currentTarget.value);
          }}
          onBlur={commitDraft}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              commitDraft();
            }
          }}
          style={{
            width: "64px",
            padding: "4px 6px",
            border: "1px solid var(--color-border, #b6b6c6)",
            borderRadius: "var(--radius-1, 4px)",
            font: "inherit",
            color: "inherit",
            background: kind === "custom" ? "var(--color-surface, #fff)" : "transparent",
          }}
        />
        <span>px</span>
      </label>
    </div>
  );
}

// ペンの太さ（細 / 中 / 太）の選択肢。描画前設定に使う。
const PEN_THICKNESS_OPTIONS: readonly {
  readonly value: PenThickness;
  readonly label: string;
}[] = [
  { value: "thin", label: "細" },
  { value: "medium", label: "中" },
  { value: "thick", label: "太" },
];

// ペンの太さ選択 UI。Confluence の例に倣い、太さの異なるドットで 3 段階を表現する。
// ドットは選択中のペン色でプレビューし、色と太さの両方を確認できるようにする。
function PenThicknessChooser({
  value,
  color,
  onChange,
}: {
  readonly value: PenThickness;
  readonly color: string;
  readonly onChange: (thickness: PenThickness) => void;
}): JSX.Element {
  // 実際の線幅（PEN_THICKNESS_PIXELS）は細かすぎて見分けづらいため、見やすい大きさへ拡大したドットで表す。
  const dotDiameter = (thickness: PenThickness): number => PEN_THICKNESS_PIXELS[thickness] * 2 + 2;
  const dotColor = isValidHexColor(color) ? color : "var(--color-text)";
  return (
    <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
      {PEN_THICKNESS_OPTIONS.map((option) => (
        <Button
          key={option.value}
          type="button"
          variant={value === option.value ? "action emphasis" : "action"}
          aria-label={`ペンの太さ: ${option.label}`}
          aria-pressed={value === option.value}
          title={`${option.label}（${PEN_THICKNESS_PIXELS[option.value]}px）`}
          onClick={() => onChange(option.value)}
        >
          <span
            aria-hidden="true"
            style={{
              display: "inline-block",
              width: `${dotDiameter(option.value)}px`,
              height: `${dotDiameter(option.value)}px`,
              borderRadius: "50%",
              background: dotColor,
              // 白系のペン色でもボタン上で視認できるよう、うっすら輪郭を付ける。
              border: "1px solid rgba(0,0,0,0.12)",
            }}
          />
        </Button>
      ))}
    </div>
  );
}

// 文字寄せ（左/中央/右）の選択肢。テキスト・付箋の挿入前設定に使う。
const TEXT_ALIGN_OPTIONS: readonly {
  readonly value: TextAlign;
  readonly label: string;
  readonly icon: string;
}[] = [
  { value: "left", label: "左寄せ", icon: FormatAlignLeftIconData },
  { value: "center", label: "中央寄せ", icon: FormatAlignCenterIconData },
  { value: "right", label: "右寄せ", icon: FormatAlignRightIconData },
];

// 文字寄せ選択 UI。左/中央/右のアイコンボタンを横並びで提供し、選択中を強調する。
function TextAlignChooser({
  value,
  onChange,
}: {
  readonly value: TextAlign;
  readonly onChange: (align: TextAlign) => void;
}): JSX.Element {
  return (
    <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
      {TEXT_ALIGN_OPTIONS.map((option) => (
        <Button
          key={option.value}
          type="button"
          variant={value === option.value ? "action emphasis" : "action"}
          aria-label={option.label}
          aria-pressed={value === option.value}
          title={option.label}
          onClick={() => onChange(option.value)}
        >
          <Icon d={option.icon} />
        </Button>
      ))}
    </div>
  );
}

// スタンプ選択ボタンの並び順（絵文字 5 種類。アバターは別途、設定済みのときだけ末尾に表示する）。
const STAMP_KIND_ORDER: readonly EmojiStampKind[] = [
  "thumbs-up",
  "fire",
  "smile",
  "hundred",
  "plus",
];

const SHAPE_OPTIONS: readonly {
  readonly kind: ShapeKind;
  readonly label: string;
  readonly icon: string;
}[] = [
  { kind: "rectangle", label: "四角", icon: Crop169IconData },
  { kind: "circle", label: "丸", icon: CircleIconData },
  { kind: "arrow", label: "矢印", icon: ArrowOutwardIconData },
];
